const blogPosts = [
  {
    slug: 'nyc-marathon-2026-guide',
    title: 'NYC Marathon 2026: The Complete Guide to Running All Five Boroughs',
    author: 'Runrora Team',
    date: '2026-03-19',
    readTime: '8 min read',
    category: 'Race Guide',
    excerpt: 'Everything you need to know about the 2026 TCS New York City Marathon — course strategy, borough-by-borough breakdown, travel tips, and how to make the most of race weekend.',
    heroImage: null,
    content: `
The TCS New York City Marathon on November 1, 2026 is the largest marathon in the world — over 50,000 finishers crossing through all five boroughs of New York City. Whether you got in through the lottery, a charity entry, or the 9+1 program, here is everything you need to know.

## The course, borough by borough

**Staten Island (Miles 0-2):** You start on the Staten Island side of the Verrazzano-Narrows Bridge. The first two miles are uphill across the bridge — do not go out fast here. The crowd energy is electric and the bridge is packed. Resist the urge to surge. You will pay for it later.

**Brooklyn (Miles 2-13):** The long, rolling stretch through Brooklyn is where most runners settle into their pace. Fourth Avenue is wide and slightly downhill — a great place to lock into your goal pace. The crowds in Williamsburg and through Bedford-Stuyvesant are some of the loudest on the course.

**Queens (Miles 13-15):** A short stretch through Long Island City after crossing the Pulaski Bridge. The crowd thins out here — stay mentally focused. This is also where the half marathon split happens, so check your time.

**The Bronx (Miles 20-21):** You enter the Bronx via the Willis Avenue Bridge around mile 20. This is a brief stretch but it comes right when the race gets hard. The crowd support picks up on East 138th Street — soak it in.

**Manhattan (Miles 16-20, 22-26.2):** You enter Manhattan twice. The first time, you run up First Avenue from mile 16 to 20 — this is one of the most iconic stretches in marathoning. The crowds are ten deep on both sides. Then after the Bronx, you come back down Fifth Avenue through Harlem and into Central Park for the finish.

## Pacing strategy

The NYC course is not flat. There are five bridges and rolling terrain throughout Central Park. Most runners should plan for negative splits — run the first half conservatively and pick it up after the Queensboro Bridge.

**Target splits for common goals:**
- **Sub-3:00**: 6:50/mile average, but run 6:55-7:00 through Brooklyn, pick up to 6:40 on First Ave
- **Sub-3:30**: 8:00/mile average, stay patient through mile 16
- **Sub-4:00**: 9:09/mile average, walk the water stations if needed
- **Sub-4:30**: 10:18/mile average, enjoy the crowds and don't stress the clock

## Race weekend logistics

**Bib pickup:** The NYC Marathon Expo is at the Javits Center, usually Thursday through Saturday before the race. You must pick up your bib in person — no race-day pickup.

**Getting to the start:** Runners take buses from near the New York Public Library (Fifth Avenue at 40th Street) starting around 5:00 AM. Fort Wadsworth on Staten Island is the staging area — plan to wait 1-3 hours before your wave.

**What to bring to the start village:** Throwaway clothes (it can be cold), breakfast, water, and your phone. There are bag check options but most runners prefer to travel light.

**After the finish:** You will exit Central Park and walk about a mile to the family reunion area. Your legs will hate you for this. Have a plan to meet your people — cell service is brutal right after the finish.

## Weather

Early November in New York City can range from 35 to 65 degrees. Check the forecast obsessively the week before. The most common mistake is overdressing — you will warm up significantly once you start running.

## Training considerations

The hills matter more than you think. The Verrazzano Bridge, the Queensboro Bridge, and the Central Park hills at miles 24-25 will test you. If you can, train on bridges and rolling terrain. If you are coming from a flat city, add hill repeats to your long runs.

The NYC Marathon is not a PR course for most runners. It is an experience course. If this is your first time, run for the experience and save your time goal for a flat race like Berlin or Chicago.

Find other runners doing the NYC Marathon 2026 on Runrora — join the race community, share your training, and connect with people running the same event.

## Sources

- [TCS New York City Marathon — Official Site](https://www.nyrr.org/tcsnycmarathon)
- [NYRR Race Results & Course Information](https://www.nyrr.org)
- [New York Road Runners — Runner Resources](https://www.nyrr.org/run)
    `.trim(),
  },
  {
    slug: 'boston-marathon-2026-course-strategy',
    title: 'Boston Marathon 2026: Course Strategy and What First-Timers Need to Know',
    author: 'Runrora Team',
    date: '2026-03-18',
    readTime: '7 min read',
    category: 'Race Guide',
    excerpt: 'The Boston Marathon on April 20, 2026 is the race every marathoner dreams of. Here is what the course actually demands and how to run it smart.',
    heroImage: null,
    content: `
The Boston Marathon on April 20, 2026 is the oldest annual marathon in the world and the only major marathon that requires a qualifying time. If you are on the start line in Hopkinton, you have already proven you belong. Now the question is how to run it well.

## The course is not what you expect

Boston is a point-to-point course that drops nearly 480 feet from Hopkinton to Boylston Street. That sounds fast, but the downhill start is actually the biggest trap on the course. Running downhill for the first 4 miles hammers your quads in a way that does not hurt until mile 18 — right when you hit the Newton hills.

## Mile-by-mile breakdown

**Miles 1-4 (Hopkinton to Ashland):** Steep downhill start. Every runner goes out too fast here. If your GPS says you are 20-30 seconds per mile faster than goal pace, you are right on track — the downhill makes your effort feel easy when it is not. Actively hold back.

**Miles 5-10 (Framingham to Natick):** Rolling terrain, gradually flattening. This is where you should settle into your actual goal pace. The crowds in the town centers are great but thin out between them.

**Miles 10-16 (Wellesley to Newton):** The famous Wellesley Scream Tunnel hits around mile 12. The noise is unbelievable. Enjoy it but do not let it pull you into surging. The course is mostly flat through here — save your energy.

**Miles 16-21 (Newton Hills):** This is where Boston is won or lost. Four hills over five miles, culminating in Heartbreak Hill at mile 20.5. Heartbreak is the steepest of the Newton Hills at roughly a 5% gradient, and it comes when your legs are most depleted.

**Strategy for the hills:** Run by effort, not pace. Accept that you will slow down 15-30 seconds per mile on the uphills. Runners who try to maintain pace through Newton blow up spectacularly by mile 22.

**Miles 21-26.2 (Boston College to Boylston):** Downhill out of Newton, then flat into Brookline and Boston. If you ran the hills smart, this is where you pass people. The right turn onto Boylston Street with 600 meters to go is one of the most iconic moments in running.

## Qualifying and registration

For the 2026 race, qualifying times range from 2:55:00 (men 18-34) to 5:20:00 (women 80+). But qualifying does not guarantee entry — Boston typically cuts several minutes below the BQ standard. For 2026, expect a cutoff of BQ minus 5-6 minutes.

## Weather

April in Boston is unpredictable. The 2018 race was 38 degrees with sideways rain. The 2012 race was 87 degrees. Prepare for anything. If the forecast is warm, adjust your goal pace by 1-2% per degree above 60.

## Race day logistics

**Getting to the start:** Runners take buses from Boston Common to Hopkinton starting around 6:30 AM. The Athletes Village in Hopkinton has water, porta-potties, and shelter. Bring throwaway clothes and a plastic bag to sit on.

**Bag drop:** You can check a bag that will be transported to the finish area in the Back Bay. Pack dry clothes and a snack.

Train on hills. Boston rewards runners who have done the quad work. If you can, run long runs on courses that include 3-4 miles of rolling hills between miles 14-20 of your long run. That specificity will pay off on race day.

Connect with other Boston 2026 runners on Runrora to share training tips and race-day plans.

## Sources

- [Boston Athletic Association — Boston Marathon](https://www.baa.org/races/boston-marathon)
- [BAA Qualifying Standards](https://www.baa.org/races/boston-marathon/qualify)
- [Boston Marathon Course Map & Elevation](https://www.baa.org/races/boston-marathon/course-map)
    `.trim(),
  },
  {
    slug: 'berlin-marathon-2026-how-to-run-a-pr',
    title: 'Berlin Marathon 2026: How to Run Your Fastest Marathon',
    author: 'Runrora Team',
    date: '2026-03-17',
    readTime: '6 min read',
    category: 'Race Guide',
    excerpt: 'The BMW Berlin Marathon on September 27, 2026 is the fastest marathon course in the world. Here is how to take advantage of it.',
    heroImage: null,
    content: `
The BMW Berlin Marathon on September 27, 2026 is where world records happen. The course is flat, the weather is typically cool, and the organization is flawless. If you are chasing a personal best, this is the race to do it.

## Why Berlin is so fast

The course is almost entirely flat — total elevation gain is only about 240 feet across 26.2 miles, spread so gradually you barely notice it. For comparison, New York has over 800 feet of climbing. The roads are wide, the turns are gentle, and there are no bridges to climb. Every men's marathon world record since 2003 has been set in Berlin, and the women's record was set here in 2023.

The weather cooperates too. Late September in Berlin averages 50-60 degrees with low humidity. That is close to the physiological sweet spot for marathon running.

## Course highlights

**Start (Tiergarten):** The race starts near the Brandenburg Gate and heads west through the Tiergarten park. The first 5K is fast and slightly downhill — do not get carried away.

**Miles 5-13:** The course loops through the western neighborhoods. Wide roads, good crowd support, and completely flat. This is where you lock into rhythm.

**Miles 13-18:** Through Kreuzberg and into the eastern side of the city. Some of the best crowd energy is here. Stay on pace — the temptation to push early is strong when you feel good on a flat course.

**Miles 18-22:** This is where the race happens. If you went out too fast, you will feel it now. If you were patient, you will start passing people. The course runs through former East Berlin — the architecture is worth looking at to stay mentally engaged.

**Miles 22-26.2:** Back toward the city center and the Brandenburg Gate finish. The last 2K has massive crowd support. If you have anything left, this is where to use it.

## Pacing strategy for a PR

Berlin rewards even pacing more than almost any other major. There are no hills to disrupt your rhythm. The key is resisting the urge to bank time early.

**The negative split approach works best here:**
- Run the first half 30-60 seconds slower than your goal split
- Pick up to goal pace from miles 13-20
- Run the last 10K at goal pace or slightly faster

If you are targeting sub-3:00, that means running 4:18-4:20 per kilometer for the first half, then 4:12-4:15 for the second half.

## Logistics

**Expo and bib pickup:** The Berlin Marathon Expo at Airport Tempelhof is one of the largest running expos in the world. It runs Thursday through Saturday. Pick up your bib early to avoid lines.

**Race morning:** The start area in the Tiergarten opens early. There are gear check areas where you can drop a bag that will be at the finish. Berlin is efficient — trains run early on race day and getting to the start is straightforward.

**After the race:** The finish area near the Brandenburg Gate has food, drinks, and medal engraving. Berlin is one of the best cities for a post-race celebration — the restaurants and beer gardens are world-class.

## Training for a flat, fast race

If your training has been hilly, that is fine — you will be well-prepared for Berlin. But add some tempo runs on flat terrain to practice holding an even pace without terrain changes to break up the effort. The mental challenge of Berlin is maintaining focus for 26 miles with no hills to structure the effort.

Join the Berlin Marathon 2026 community on Runrora to find training partners and share your PR goals.

## Sources

- [BMW Berlin Marathon — Official Site](https://www.bmw-berlin-marathon.com/en/)
- [Berlin Marathon Course & Records](https://www.bmw-berlin-marathon.com/en/your-race/course/)
- [World Athletics — Marathon Records](https://worldathletics.org/records/all-time-toplists/road-running/marathon/outdoor/men/senior)
    `.trim(),
  },
  {
    slug: 'chicago-marathon-2026-first-timer-guide',
    title: 'Chicago Marathon 2026: The First-Timer\'s Guide to a Great Race',
    author: 'Runrora Team',
    date: '2026-03-16',
    readTime: '6 min read',
    category: 'Race Guide',
    excerpt: 'The Bank of America Chicago Marathon on October 11, 2026 is one of the best first marathons in the world. Here is everything you need to know.',
    heroImage: null,
    content: `
The Bank of America Chicago Marathon on October 11, 2026 draws over 45,000 runners through a flat, fast course that tours the best neighborhoods of Chicago. It is widely considered one of the best marathons for first-timers and PR chasers alike.

## Why Chicago is great for first-timers

The course is flat — only about 240 feet of total elevation change across the entire marathon. The crowd support is massive, with an estimated 1.7 million spectators lining the course. And the organization is excellent — Chicago runs like a well-oiled machine.

The lottery is easier to get into than New York or London, and there is no qualifying time required. If you want to run a World Marathon Major, Chicago is one of the most accessible options.

## Course overview

The course is a large loop that starts and finishes in Grant Park, along the Lake Michigan lakefront. You run through 29 neighborhoods, each with its own energy and crowd vibe.

**Miles 1-5 (Loop and North Side):** Starting in Grant Park, you head north through the Loop and into Lincoln Park. The first few miles are crowded — be patient and avoid weaving. The energy from the spectators in Old Town and Lincoln Park is outstanding.

**Miles 5-13 (Lakeview, Boystown, Wrigleyville):** Running through some of Chicago's most vibrant neighborhoods. Boystown around mile 8 is one of the loudest sections on the course. You will hear music from every bar and restaurant.

**Miles 13-18 (Little Italy, Pilsen, Chinatown):** The course turns south and runs through some of Chicago's most diverse neighborhoods. Pilsen has incredible crowd energy with live mariachi bands. Chinatown around mile 21 is colorful and loud.

**Miles 18-22 (Bronzeville, South Side):** The crowd thins slightly here, and this is where the race gets mentally tough. If you have been disciplined with your pacing, you will feel strong. If you went out too fast, this is where you pay for it.

**Miles 22-26.2 (Back to Grant Park):** Heading north back toward the finish. The last two miles along Michigan Avenue have huge crowds. The finish in Grant Park with the Chicago skyline behind you is spectacular.

## Weather warning

October in Chicago can be unpredictable. The most common problem is heat — several editions of the Chicago Marathon have seen temperatures in the upper 70s or even 80s, which is dangerous for marathon running. Check the forecast and have a backup pacing plan for warm conditions. In 2007, the race was stopped mid-event due to extreme heat.

Ideal conditions are 45-55 degrees with cloud cover. If the forecast is above 65, slow your goal pace by 1-2 minutes.

## Race day logistics

**Expo:** McCormick Place Convention Center, typically Friday and Saturday. Bib pickup required in person.

**Race morning:** Grant Park opens early. Corrals are organized by projected finish time. Get to your corral early — the walk from gear check to the start can take 20-30 minutes.

**Gear check:** Bag drop is available near the start. You will pick up your bag near the finish in Grant Park.

**Spectator tips:** Michigan Avenue (mile 24-25) and the finish area in Grant Park are the best spectator spots. The CTA trains run early on race morning — tell your people to take the train, not drive.

## Pacing

Chicago is a PR course. The flat terrain means you can run even splits from start to finish. Do not go out fast in the first mile — the downhill start into the tunnel under the buildings creates a false sense of speed.

Find other Chicago Marathon 2026 runners on Runrora and start building your race community today.

## Sources

- [Bank of America Chicago Marathon — Official Site](https://www.chicagomarathon.com)
- [Chicago Marathon Course Overview](https://www.chicagomarathon.com/runners/course-amenities/)
- [Abbott World Marathon Majors](https://www.worldmarathonmajors.com)
    `.trim(),
  },
  {
    slug: 'london-marathon-2026-guide',
    title: 'London Marathon 2026: Course Guide and Race Day Tips',
    author: 'Runrora Team',
    date: '2026-03-15',
    readTime: '6 min read',
    category: 'Race Guide',
    excerpt: 'The TCS London Marathon on April 26, 2026 is one of the most iconic races on earth. Here is what to expect from the course, the crowds, and race weekend.',
    heroImage: null,
    content: `
The TCS London Marathon on April 26, 2026 is one of the six World Marathon Majors and one of the most oversubscribed races in the world. Only about 15% of ballot applicants get a spot, making entry a prize in itself.

## The course

London is a point-to-point course starting in Greenwich and finishing on The Mall in front of Buckingham Palace. The course is mostly flat with a few gentle undulations in the first half.

**Miles 1-5 (Greenwich to Woolwich):** Three separate start areas converge around mile 3. The early miles run through southeast London with good crowd support. The terrain is flat and the roads are wide.

**Miles 5-12 (Woolwich to Tower Bridge):** Running through Charlton and Greenwich. The crowd support builds steadily. The Cutty Sark at mile 6 is the first major landmark — the energy here is incredible.

**Miles 12-14 (Tower Bridge):** Crossing Tower Bridge around mile 12 is the defining moment of the London Marathon. The crowds are enormous, the view is stunning, and the noise is deafening. This is where every first-timer falls in love with the race.

**Miles 14-21 (Docklands):** The course loops through Canary Wharf and the Docklands. This section is flatter but the crowd thins out slightly. The out-and-back nature of the Docklands loop means you can see runners ahead of and behind you — use this to stay motivated.

**Miles 21-26.2 (Embankment to The Mall):** Running along the Thames Embankment with the London Eye, Big Ben, and the Houses of Parliament as your backdrop. The final stretch down The Mall to the finish line in front of Buckingham Palace is one of the most iconic finishes in world running.

## Getting a spot

The ballot opens in April each year for the following year's race. Your chances of getting in through the ballot are very low — roughly 1 in 50, with over a million applicants in recent years. Other options include charity entries (most charities require a minimum fundraising commitment of 2,000-2,500 pounds), Championship entry (a fast qualifying time — 2:38 for men, 3:10 for women), and Good for Age entry (age-graded qualifying times).

## Weather and kit

Late April in London typically means temperatures between 45-60 degrees with a decent chance of rain. Pack a throwaway rain poncho for the start. Overdressing is a common mistake — the race starts in the morning and you will warm up quickly.

## Logistics

**Expo:** The London Marathon Expo is at ExCeL London in the Docklands, usually Wednesday through Saturday before the race. Registration and bib pickup are here.

**Getting to the start:** Race-day trains run from central London to the start areas in Greenwich, Blackheath, and Woolwich. Plan to arrive early — the start area opens about 2 hours before the gun.

**After the finish:** The finish area on The Mall has bag collection, food, and drink. The walk to meet spectators can be long — agree on a specific meeting point in St. James's Park.

## Unique London traditions

- The London Marathon is the largest annual fundraising event in the world — runners raise over 60 million pounds for charity each year
- Fancy dress costumes are a big part of the culture — you will see rhinos, dinosaurs, and entire houses running past you
- The "They said I could never" signs at Cutty Sark will make you emotional every time

London is a race you run for the experience more than the time. The crowds, the landmarks, and the atmosphere are unmatched. But if you do want a fast time, the flat second half is very runnable — just be patient through the first 12 miles.

Connect with other London Marathon runners on Runrora to share your training journey.

## Sources

- [TCS London Marathon — Official Site](https://www.tcslondonmarathon.com)
- [London Marathon Entry Information](https://www.tcslondonmarathon.com/enter/how-to-enter)
- [London Marathon Charity Places](https://www.tcslondonmarathon.com/enter/charity-entry)
    `.trim(),
  },
  {
    slug: 'utmb-2026-guide-ultra-trail-mont-blanc',
    title: 'UTMB 2026: Everything You Need to Know About Ultra-Trail Mont-Blanc',
    author: 'Runrora Team',
    date: '2026-03-14',
    readTime: '8 min read',
    category: 'Race Guide',
    excerpt: 'UTMB Mont-Blanc on August 28, 2026 is the biggest trail ultramarathon in the world. Here is how entry works, what the course demands, and how to prepare.',
    heroImage: null,
    content: `
UTMB Mont-Blanc on August 28, 2026 is the crown jewel of trail ultrarunning. The 171-kilometer course circles the entire Mont-Blanc massif through France, Italy, and Switzerland with over 10,000 meters of elevation gain. Finishing UTMB is one of the most significant achievements in endurance sport.

## The UTMB distances

UTMB week actually includes several races of different distances:

- **UTMB** — 171 km, 10,000 m elevation gain, 46:30 cutoff
- **CCC** — 101 km, 6,100 m elevation gain, 26:30 cutoff
- **TDS** — 145 km, 9,100 m elevation gain, 40:00 cutoff
- **OCC** — 55 km, 3,425 m elevation gain, 14:30 cutoff
- **MCC** — 40 km, 2,300 m elevation gain, 11:00 cutoff

If this is your first UTMB week, the OCC or CCC are excellent starting points. The full UTMB requires significant ultra experience and a proven track record at shorter mountain distances.

## Entry system: Running Stones

UTMB uses a points-based entry system called Running Stones. You earn points by finishing qualifying races on the UTMB Index throughout the year. For the main UTMB race, you need to accumulate enough points from races rated at the appropriate difficulty level.

The lottery draw happens in January. Having the required points gets you into the lottery — it does not guarantee a spot. Popular distances like UTMB and CCC are heavily oversubscribed.

## The UTMB course

**Chamonix to Les Contamines (0-31 km):** The race starts in the center of Chamonix at 6 PM on Friday. You climb immediately up to Le Delevret before descending to Saint-Gervais and then Les Contamines. Most runners hit this first aid station in 3-5 hours. The temptation to go fast while fresh is strong — resist it.

**Les Contamines to Courmayeur (31-80 km):** The biggest climb of the race — Col de la Seigne at 2,516 meters — comes in this section. You cross from France into Italy and descend into Courmayeur. This is where you will likely experience your first night on the course. Courmayeur has a full aid station with beds if you need a nap.

**Courmayeur to Champex-Lac (80-128 km):** The Grand Col Ferret crossing into Switzerland is stunning at sunrise. The terrain is technical and the descents are punishing on your quads. By Champex-Lac, you will have been running for 20-30 hours.

**Champex-Lac to Chamonix (128-171 km):** The final section includes two more significant climbs. The last big effort is the climb to La Tete aux Vents before the final descent into Chamonix. Running down the streets of Chamonix to the finish line with the crowds cheering is one of the greatest moments in ultrarunning.

## Gear requirements

UTMB has mandatory gear that is checked before the race:

- Waterproof jacket and pants
- Warm mid-layer (fleece or down)
- Emergency blanket
- Headlamp with backup batteries
- Cup (no disposable cups on course)
- Minimum 1 liter water carrying capacity
- Food reserve
- Phone with emergency numbers stored
- ID and health certificate

Invest in good gear and test everything in training. A headlamp failure at 3 AM above 2,000 meters is not the time to discover your battery is dead.

## Training for UTMB

You need a minimum of 2-3 years of trail ultra experience before attempting UTMB. Your training should include:

- **Weekly vertical**: Build to 3,000-4,000 meters of climbing per week in peak training
- **Back-to-back long days**: Run 6-8 hours Saturday and 4-6 hours Sunday to simulate the fatigue
- **Night running**: Practice running with a headlamp on technical terrain
- **Altitude**: If you do not live at altitude, arrive in Chamonix 3-5 days early to acclimatize

## DNF rates

UTMB has a typical DNF rate of 30-40%. This is not a race where finishing is guaranteed, even for experienced ultra runners. Weather in the Alps can change rapidly — snow, wind, and freezing rain are all possible in late August at altitude.

The runners who finish are the ones who manage their effort, eat consistently, and stay mentally flexible when things go wrong.

Find UTMB 2026 runners on Runrora to share training plans and course tips.

## Sources

- [UTMB Mont-Blanc — Official Site](https://utmbmontblanc.com)
- [UTMB Running Stones Entry System](https://utmb.world/running-stones)
- [UTMB Mandatory Equipment List](https://utmbmontblanc.com/en/page/21/equipment.html)
    `.trim(),
  },
  {
    slug: 'western-states-100-2026-guide',
    title: 'Western States 100 2026: The Ultimate Guide to America\'s Oldest 100-Miler',
    author: 'Runrora Team',
    date: '2026-03-13',
    readTime: '7 min read',
    category: 'Race Guide',
    excerpt: 'The Western States 100 on June 27, 2026 is the most iconic ultramarathon in North America. Here is what the course demands and how runners prepare.',
    heroImage: null,
    content: `
The Western States Endurance Run on June 27, 2026 covers 100.2 miles from Olympic Valley to Auburn, California, following the historic Western States Trail through the Sierra Nevada mountains. It is the oldest 100-mile trail race in the world and arguably the most prestigious.

## Getting in

Western States is one of the hardest races in the world to enter. The lottery typically has over 11,000 applicants for 369 spots (with only about 257 available via lottery). Your odds improve each year you apply without getting in — tickets roll over. Even so, many runners wait 5-10 years before their name is drawn.

To enter the lottery, you must complete a qualifying 100-mile race within the time cutoff (usually 30 hours) in the previous year. The list of qualifying races is posted on the WS100 website.

## The course

**Olympic Valley to Robinson Flat (0-30 miles):** The race starts at 5 AM and immediately climbs from the valley floor to Emigrant Pass at 8,750 feet — the highest point on the course. Snow is possible in June. The climb to the Escarpment is steep and most runners power-hike it. Robinson Flat at mile 30 is the first major crew-access point.

**Robinson Flat to Foresthill (30-62 miles):** A long rolling section through the forests with several canyons to cross. The descents are runnable but steep. You will lose thousands of feet of elevation on these canyon descents — your quads need to be ready for this. Dusty Corners, Last Chance, and Devils Thumb are infamous sections.

**Foresthill to the River (62-78 miles):** The long downhill from Foresthill to the American River crossing is 3,000 feet of descent in 16 miles. This is where the race breaks runners. If your quads are shredded, this descent will be brutal. If you trained for downhill running, you can make up significant time here.

**The River Crossing (mile 78):** You wade across the American River — waist to chest deep depending on the water level. It is cold and shocking. Many runners change shoes on the other side. Having a pacer from here to the finish is allowed and highly recommended.

**The River to Auburn (78-100.2 miles):** The final 22 miles climb out of the river canyon and run along exposed ridges. This section has several steep climbs and descents. The temperature at the river can reach 100+ degrees during hot years. The last 3 miles into Auburn are on the road, and the finish at the Placer High School track is emotional beyond words.

## Cutoffs

The overall cutoff is 30 hours (11 AM Sunday). But there are intermediate cutoffs that eliminate runners throughout the race. The critical cutoffs are:

- Robinson Flat (mile 30): 9 hours 10 minutes (2:10 PM)
- Foresthill (mile 62): 18 hours 45 minutes (11:45 PM)
- The River (mile 78): 24 hours (5:00 AM Sunday)
- Auburn (mile 100.2): 30 hours (11:00 AM Sunday)

About 75-85% of starters finish in a typical recent year, though hot years push that number down significantly. Heat is the biggest factor in finish rates — hot years see significantly more DNFs.

## Heat training

Western States is notorious for heat. Temperatures in the canyons can exceed 100 degrees. Heat training is not optional — it is essential. Strategies include:

- **Sauna sessions**: 20-30 minutes, 3-4 times per week for the 2-3 weeks before the race
- **Overdressing on runs**: Run in extra layers during training to acclimate
- **Ice bandanas and cooling**: Learn to use the ice available at aid stations effectively

## Crew and pacers

Western States allows pacers from mile 62 (Foresthill) and crew access at specific aid stations. A good crew can make or break your race. Brief them on what you need at each station — calories in, bottles refilled, clothing changes — and keep crew stops under 5 minutes.

Join the Western States 2026 community on Runrora to connect with other runners preparing for the race.

## Sources

- [Western States Endurance Run — Official Site](https://www.wser.org)
- [Western States Course Description](https://www.wser.org/course/)
- [Western States Lottery & Qualifying Races](https://www.wser.org/lottery/)
    `.trim(),
  },
  {
    slug: 'brooklyn-half-marathon-2026-guide',
    title: 'Brooklyn Half Marathon 2026: Course Guide for the RBC Brooklyn Half',
    author: 'Runrora Team',
    date: '2026-03-12',
    readTime: '5 min read',
    category: 'Race Guide',
    excerpt: 'The RBC Brooklyn Half on May 16, 2026 is one of the largest half marathons in the country. Here is what to expect from the course, the vibe, and race day.',
    heroImage: null,
    content: `
The RBC Brooklyn Half Marathon on May 16, 2026 is one of the largest and most popular half marathons in the United States, drawing over 27,000 runners through Prospect Park and along the Ocean Parkway bike path to a finish on the Coney Island Boardwalk.

## The course

**Miles 1-3 (Prospect Park):** The race starts inside Prospect Park and does a loop of the park's main drive. The park roads are rolling with a few moderate hills — nothing steep, but enough to challenge runners who go out too fast. The trees provide shade, which is welcome if it is a warm morning.

**Miles 3-5 (Grand Army Plaza to Ocean Parkway):** You exit Prospect Park near Grand Army Plaza and head south onto Ocean Parkway. The wide, tree-lined median of Ocean Parkway is one of the most unique race settings in New York. The crowd here is a mix of local residents, families, and runners cheering from their stoops.

**Miles 5-11 (Ocean Parkway):** This is a long, straight, flat stretch south toward Coney Island. The shade disappears and if it is a warm day, this section can feel exposed. Find a group running your pace and tuck in. The course is wide enough that you will not feel crowded.

**Miles 11-13.1 (Coney Island Boardwalk):** The course turns onto the Coney Island Boardwalk for the final two miles. Running on the boardwalk with the ocean on your right and the amusement park on your left is one of the best finishes in any half marathon. The boardwalk surface is slightly uneven — watch your footing but enjoy the moment.

## Registration

The Brooklyn Half is extremely popular and uses a lottery drawing system. The drawing window typically opens in January, with results announced shortly after. There are also guaranteed entry pathways through qualifying times and NYRR volunteering.

The Brooklyn Half is also a qualifier for the NYC Marathon through the 9+1 program. Completing this race counts as one of your qualifying races.

## Race day logistics

**Start:** The race starts early — typically 7 AM. Getting to Prospect Park is easy via the B, Q, or 2/3 trains to Grand Army Plaza or Eastern Parkway.

**Gear check:** NYRR provides bag check at the start in Prospect Park. Your bag will be transported to the finish area at Coney Island — a different location from the start. Plan accordingly.

**After the finish:** You finish at Coney Island. Grab a post-race Nathan's hot dog (it is tradition), ride the Cyclone if your legs can handle it, or take the D/F/N/Q train back to Manhattan.

## Weather

Mid-May in New York can be anything from 50 to 85 degrees. The 2018 Brooklyn Half saw temperatures above 80 degrees and NYRR added medical stations along the course. If it is warm, slow your pace, take water at every station, and douse yourself at the sponge stations.

## Pacing tips

The Prospect Park loop at the start has rolling hills, so do not chase your goal pace in the first 3 miles. Settle in on Ocean Parkway and run even effort. Save something for the boardwalk — you want to enjoy that finish.

Find other Brooklyn Half 2026 runners on Runrora and start connecting with your race community.

## Sources

- [NYRR RBC Brooklyn Half Marathon](https://www.nyrr.org/races/rbcbrooklynhalf)
- [NYRR 9+1 NYC Marathon Qualifying Program](https://www.nyrr.org/run/guaranteed-entry/tcs-new-york-city-marathon-9plus1-program)
- [NYRR Race Calendar](https://www.nyrr.org/races)
    `.trim(),
  },
  {
    slug: 'comrades-marathon-2026-ultimate-guide',
    title: 'Comrades Marathon 2026: The Ultimate Guide to the World\'s Greatest Ultra',
    author: 'Runrora Team',
    date: '2026-03-11',
    readTime: '7 min read',
    category: 'Race Guide',
    excerpt: 'The Comrades Marathon on June 14, 2026 is the oldest and largest ultramarathon in the world. Here is everything you need to know about running 87 km through KwaZulu-Natal.',
    heroImage: null,
    content: `
The Comrades Marathon on June 14, 2026 is an approximately 87-kilometer ultramarathon between Durban and Pietermaritzburg in South Africa's KwaZulu-Natal province. Founded in 1921, it is the oldest and largest ultramarathon in the world, with over 20,000 runners each year.

## Up run vs down run

Comrades alternates direction each year. The "up run" goes from Durban to Pietermaritzburg (net uphill). The "down run" goes from Pietermaritzburg to Durban (net downhill, but with brutal uphills mid-race). Check which direction 2026 is — this significantly affects your pacing and training strategy.

The down run is generally considered easier for first-timers, but the descents punish your quads late in the race. The up run is more honest — you know exactly what you are getting into.

## The five big hills

Regardless of direction, you will encounter five major hills. In the up-run direction:

- **Cowies Hill** — The first major climb, about 10 km from the start in Durban. Steep but short.
- **Fields Hill** — The steepest and most feared hill on the course. In the up direction, it is a brutal climb. In the down direction, it destroys quads.
- **Botha's Hill** — A long, grinding climb through the midlands.
- **Inchanga** — A sharp climb that comes when you are already tired.
- **Polly Shortts** — The final significant hill near Pietermaritzburg.

## Cutoff times and medals

Comrades has strict cutoffs and a tiered medal system:

- **Gold** — Top 10 men and women
- **Wally Hayward** (men) / **Isavel Roche-Kelly** (women) — Sub 6:00:00
- **Silver** — Sub 7:30:00
- **Bill Rowan** — Sub 9:00:00
- **Robert Mtshali** — Sub 10:00:00
- **Bronze** — Sub 11:00:00
- **Vic Clapham** — Sub 12:00:00 (the overall cutoff)

The gun goes off at 5:30 AM and the race is over at exactly 12 hours. If you are not across the finish line when the final gun fires, you do not get a medal — even if you are 10 meters away. This is one of the most dramatic moments in sports. Arrive at the finish with time to spare.

## Qualifying

To enter Comrades, you need to have completed a marathon in under 5:00:00 within the qualifying period. This is a generous qualifier — most runners who can finish a marathon in under 5 hours can complete Comrades with proper training.

## Training

Comrades requires a different approach than marathon training:

- **Build weekly volume**: Peak weeks should reach 90-120 km. You need time on your feet more than speed.
- **Back-to-back long runs**: A 35 km Saturday followed by a 25 km Sunday teaches your body to run on tired legs.
- **Hill training**: If the race is an up run, train on uphills. If it is a down run, train on downhills — downhill running causes more muscle damage.
- **Run by time, not distance**: Many Comrades runners train by hours rather than kilometers. A 4-hour long run teaches you to manage effort over time.

## Race day nutrition

You will be running for 8-12 hours. Nutrition is not optional — it is survival. Comrades aid stations (called "tables") are well-stocked with Coke, water, sachets, and food. Many experienced runners rely on Coke in the second half for quick calories and caffeine.

Plan to consume 200-300 calories per hour from a mix of gels, real food, and whatever the aid stations offer. Practice your nutrition strategy in training — your stomach needs to be trained for this.

## The Comrades experience

Comrades is more than a race. The crowd support in South Africa is legendary — entire communities turn out to cheer, hand out food, and spray runners with water. The camaraderie between runners is unlike any other event. Faster runners who have already finished often come back out to run slower runners in for the final kilometers.

The energy at the finish is overwhelming. Whether you finish in 6 hours or 11 hours and 59 minutes, you are a Comrades finisher — and that means something in the global running community.

Find other Comrades 2026 runners on Runrora and share your training journey.

## Sources

- [Comrades Marathon Association — Official Site](https://www.comrades.com)
- [Comrades Marathon Qualifying Standards](https://www.comrades.com/how-to-enter/qualifying)
- [Comrades Marathon Route & Cutoffs](https://www.comrades.com/race-info/route)
    `.trim(),
  },
  {
    slug: 'leadville-100-2026-guide',
    title: 'Leadville Trail 100 Run 2026: How to Prepare for the Race Across the Sky',
    author: 'Runrora Team',
    date: '2026-03-10',
    readTime: '7 min read',
    category: 'Race Guide',
    excerpt: 'The Leadville Trail 100 on August 22, 2026 is one of the most iconic 100-mile races in the world. Here is what the altitude, the course, and the cutoffs actually demand.',
    heroImage: null,
    content: `
The Leadville Trail 100 Run on August 22, 2026 covers 100 miles through the Colorado Rockies at altitudes between 9,200 and 12,600 feet. Known as the "Race Across the Sky," Leadville is one of the four original 100-mile trail races and remains one of the most coveted finishes in ultrarunning.

## Why altitude changes everything

Leadville's lowest point is higher than the summit of most East Coast mountains. At 10,000+ feet, the air has about 30% less oxygen than at sea level. This means your heart rate will be higher at every pace, your recovery between efforts will be slower, and you will fatigue faster than at lower altitudes.

If you live below 5,000 feet, altitude is the single biggest factor in your race. No amount of fitness compensates for not being acclimated.

## Altitude preparation

- **Best option**: Arrive in Leadville 2-3 weeks before the race and live/train at altitude
- **Good option**: Arrive 5-7 days before and do easy running only
- **Minimum**: Arrive 2-3 days before — this is enough to partially acclimate but you will still feel the altitude
- **Altitude tent**: If you have access to one, sleeping in a simulated altitude tent for 4-6 weeks before the race can help significantly

## The course

The course is an out-and-back from the town of Leadville to the turnaround at Winfield (mile 50) and back.

**Leadville to May Queen (mile 13.5):** A flat, runnable section around Turquoise Lake. Most runners start conservatively here — it is tempting to run fast while the terrain is easy, but banking time at altitude backfires.

**May Queen to Twin Lakes (mile 40):** The course climbs over Sugarloaf Pass and descends to the Twin Lakes aid station. Your crew can access you at Twin Lakes — this is a critical stop for nutrition and gear changes. The climb over Hope Pass starts here.

**Hope Pass (miles 40-50-60):** This is the defining feature of Leadville. Hope Pass rises to approximately 12,600 feet — the highest point on the course. You cross it twice: once on the way to Winfield and once on the way back. The climb is relentless — 3,400 feet of gain in about 5.5 miles. Most runners power-hike the entire ascent. The descent to Winfield is steep and rocky.

**Winfield turnaround (mile 50):** The halfway point. You have 14 hours to reach Winfield to stay in the race. Many runners take 10-20 minutes here to eat, change socks, refill bottles, and mentally reset for the return trip. The hardest part of the race is ahead — you must climb back over Hope Pass.

**The return (miles 50-100):** Everything in reverse, but on tired legs and in the dark. The second crossing of Hope Pass, typically at night, is where many runners drop. The descent from Hope Pass back to Twin Lakes requires careful footing on rocky switchbacks with only a headlamp. The final 13 miles around Turquoise Lake to the finish in Leadville should be runnable — but at mile 87, nothing feels runnable.

## Cutoffs

- **May Queen outbound (mile 12.6)**: 3 hours 45 minutes (7:45 AM)
- **Twin Lakes outbound (mile 37.9)**: 9 hours 30 minutes (1:30 PM)
- **Winfield (mile 50)**: 14 hours (6:00 PM)
- **Twin Lakes inbound (mile 62)**: 18 hours (10:00 PM)
- **May Queen inbound (mile 87.8)**: 26 hours 30 minutes (6:30 AM)
- **Finish (mile 100)**: 30 hours (10:00 AM)

The sub-25-hour finishers receive the coveted large belt buckle. Sub-30 finishers receive the standard buckle. Both are lifetime achievements.

## Crew and pacers

Crew access is available at major aid stations. Pacers are allowed from Twin Lakes outbound (mile 60). A good pacer who knows the course and can keep you moving through the night is worth minutes per mile. Choose someone who has run ultras and knows when to push you and when to be patient.

## Training

Leadville demands three things: altitude fitness, climbing ability, and the mental toughness to run all night on a course you have already seen in daylight. Train with:

- **Long climbs**: Build to runs with 5,000-8,000 feet of climbing
- **Back-to-back long runs**: 30+ miles Saturday, 15-20 miles Sunday
- **Night running**: Practice running at 2 AM when your body wants to sleep
- **Power hiking**: You will hike most of the uphills — practice hiking at a fast pace (15-17 min/mile uphill)

Connect with other Leadville 2026 runners on Runrora to share training tips and race strategy.

## Sources

- [Leadville Race Series — Official Site](https://www.leadvilleraceseries.com)
- [Leadville Trail 100 Run Course Information](https://www.leadvilleraceseries.com/run/leadvilletrail100run/)
- [Leadville 100 Cutoff Times & Aid Stations](https://www.leadvilleraceseries.com/run/leadvilletrail100run/course/)
    `.trim(),
  },
  {
    slug: 'javelina-jundred-2026-best-first-100-miler',
    title: 'Javelina Jundred 2026: Why It\'s the Best First 100-Miler',
    author: 'Runrora Team',
    date: '2026-03-09',
    readTime: '6 min read',
    category: 'Race Guide',
    excerpt: 'The Javelina Jundred on October 31, 2026 is one of the most popular first 100-milers in the world. Here is why it is the perfect race to earn your first buckle.',
    heroImage: null,
    content: `
The Javelina Jundred on October 31, 2026 in Fountain Hills, Arizona is widely considered the best first 100-mile race in the world. With a runnable course, generous cutoffs, and an incredible community atmosphere, Javelina is where hundreds of runners earn their first 100-mile buckle every year.

## Why Javelina for your first 100

**Beginner-friendly design**: The course, the cutoffs, and the festival atmosphere are all designed to help first-timers finish. The loop format means you are never far from your crew and the main aid station.

**Runnable terrain**: The course is on smooth desert trails with minimal technical footing. You can actually run most of this course, unlike mountain 100s where you are hiking half the distance.

**Generous cutoffs**: You have 30 hours to finish. With a relatively flat course, this gives most runners a comfortable margin.

**Loop format**: The course is a loop run five times. This means you pass through the main aid station every loop, your crew can see you frequently, and you always know exactly where you are on the course.

**Community**: Javelina has a festival atmosphere. The main aid station (Javelina Jeadquarters) has music, costumes (it is Halloween weekend), and an energy that keeps runners moving even at 3 AM. Spectators and crews create a party atmosphere that makes the dark hours more bearable.

## The course

The course is a loop at McDowell Mountain Regional Park — the first loop is about 22.3 miles (including the Escondido Trail), and loops 2-5 are about 19.5 miles each, totaling just over 100 miles.

**The loop**: You leave Javelina Jeadquarters heading south on smooth desert trail. The terrain is rolling — not flat, but no major climbs. There are three intermediate aid stations per loop (Coyote Camp, Jackass Junction, and Rattlesnake Ranch), so you are never more than about 6 miles from support.

**Navigation**: The loop format means you learn the course quickly. By loop 3, you will know every turn, every rock, and every aid station setup. This familiarity is psychologically comforting when you are 60+ miles in.

**Night running**: You will run at least 2-3 loops in the dark. The desert at night is beautiful — the stars above the Sonoran Desert are incredible. The trails are smooth enough that a good headlamp is all you need.

## Weather

Late October in the Arizona desert means warm days (70-85 degrees) and cool nights (45-55 degrees). The heat is manageable compared to summer, but you still need to respect it:

- Start with electrolytes from mile 1
- Carry at least 40 oz of fluid between aid stations
- Wear sun protection for the first two loops (daylight hours)
- Have warm layers ready for the night — desert temperatures drop fast after sunset

## Pacing strategy

The loop format makes pacing straightforward. A common strategy:

- **Loop 1 (miles 0-20)**: Run easy, well under goal pace. This loop should feel boring. That is correct.
- **Loop 2 (miles 20-40)**: Settle into rhythm. Eat consistently. This is your marathon pace for the day.
- **Loop 3 (miles 40-60)**: This is where the race starts. Run the uphills, power-hike if needed. Keep eating.
- **Loop 4 (miles 60-80)**: Nighttime. Run what you can, walk what you need to. Focus on forward progress and calories.
- **Loop 5 (miles 80-100)**: Whatever it takes. Run when you can. Walk when you must. You will finish.

## Crew and pacers

Pacers are allowed from mile 60 (start of loop 4). Crew access at Jeadquarters every loop means your crew can keep you stocked with fresh gear, food, and motivation.

A crew at Javelina is helpful but not required. The aid stations are well-stocked and the volunteers are experienced. Many runners finish their first 100 at Javelina with no crew at all.

## Registration

Javelina typically opens registration in the spring and sells out within hours. Set a calendar reminder and be ready when registration opens. There is no lottery — it is first-come, first-served.

If you are thinking about your first 100-miler, Javelina is the one. The course is forgiving, the community is supportive, and the finish rate proves that this race wants you to succeed.

Find other Javelina Jundred 2026 runners on Runrora and start your 100-mile journey together.

## Sources

- [Javelina Jundred — Official Site](https://www.javelinajundred.com)
- [Aravaipa Running — Javelina Jundred](https://www.aravaiparunning.com/javelina-jundred/)
- [McDowell Mountain Regional Park — Trail Information](https://www.maricopacountyparks.net/park-locator/mcdowell-mountain-regional-park/)
    `.trim(),
  },
  {
    slug: 'how-to-choose-your-first-ultramarathon',
    title: 'How to Choose Your First Ultramarathon',
    author: 'Runrora Team',
    date: '2026-03-19',
    readTime: '7 min read',
    category: 'Training',
    excerpt: 'Ready to go beyond the marathon? Here\'s how to pick the right ultra for your first attempt — from 50Ks to 100-milers.',
    heroImage: null,
    content: `
Going beyond 26.2 miles is a big leap, but it doesn't have to be overwhelming. The key is choosing the right race for where you are right now — not where you hope to be in two years.

## Start with distance

If you've run a marathon, a 50K (31 miles) is the most natural next step. It's only 5 more miles, and many 50Ks are on trails, which means softer surfaces and a more forgiving pace. Don't feel pressure to jump straight to 100 miles — that's a different beast entirely.

**Good first ultras by distance:**
- **50K**: Chuckanut 50K (Bellingham, WA), Bandera 50K (Bandera, TX)
- **50 Mile**: JFK 50 Mile (Boonsboro, MD), Sean O'Brien 50 Mile (Malibu, CA)
- **100K**: Gorge Waterfalls 100K (Cascade Locks, OR), Canyons 100K (Foresthill, CA)

## Consider the terrain

Road ultras exist, but most ultras are on trails. If you've never run trails, start training on them months before race day. Technical terrain (rocks, roots, steep grades) demands different muscles and a different mindset than road running.

For your first ultra, look for courses described as "runnable" or "fast" rather than "technical" or "mountainous." Save Hardrock and Western States for later.

## Elevation matters more than distance

A flat 50-miler can be easier than a mountainous 50K. Check the elevation profile before you sign up. Anything over 5,000 feet of climbing in a 50K is considered significant. For reference, the Broken Arrow Skyrace packs 10,000 feet of climbing into 52K — that's a very different race than a flat canal-path ultra.

## Cutoff times tell you a lot

Generous cutoffs (16+ hours for a 50-miler, 30+ hours for a 100-miler) signal a race that welcomes first-timers. Tight cutoffs mean the race expects experienced ultra runners. The Javelina Jundred, for example, is known for high finish rates and a welcoming atmosphere — a great first 100.

## Aid stations and crew access

Ultras with frequent aid stations (every 4-6 miles) are more forgiving for first-timers. You can carry less and recover more often. Also check if the race allows crew and pacers — having someone meet you at mile 60 of a 100-miler can make the difference between finishing and dropping.

## Weather and time of year

Desert races in October (like Javelina) offer cooler temps than you'd expect. Mountain races in July (like Hardrock) can mean snow at altitude. Spring races in the Pacific Northwest often mean mud. Match the race conditions to what you've trained in.

## The bottom line

Your first ultra should challenge you without crushing you. Pick a distance that stretches you by 20-30% beyond your longest race, on terrain you've trained for, with generous cutoffs and good aid. Save the bucket-list sufferfests for when you've got a few finishes under your belt.

Browse ultramarathons and trail races on Runrora to find the right one for you.
    `.trim(),
  },
  {
    slug: '2026-marathon-calendar-biggest-races',
    title: '2026 Marathon Calendar: The Biggest Races You Don\'t Want to Miss',
    author: 'Runrora Team',
    date: '2026-03-17',
    readTime: '6 min read',
    category: 'Race Calendar',
    excerpt: 'From the World Marathon Majors to fast destination races, here are the marathons and halfs to put on your calendar for 2026.',
    heroImage: null,
    content: `
Whether you're chasing a PR, collecting World Marathon Major stars, or just looking for an unforgettable race experience, 2026 has an incredible lineup. Here's what's on the calendar.

## The Abbott World Marathon Majors

These six races are the pinnacle of marathon running. Finishing all six earns you a Six Star Medal — one of the most coveted achievements in distance running.

- **Tokyo Marathon** — March 1, 2026
- **Boston Marathon** — April 20, 2026
- **London Marathon** — April 26, 2026
- **Berlin Marathon** — September 27, 2026
- **Chicago Marathon** — October 11, 2026
- **New York City Marathon** — November 1, 2026

Each Major has its own personality. Tokyo is precise and orderly. Boston is all about the crowd energy on Heartbreak Hill. Berlin is flat and fast — the course where most world records fall. New York is the most diverse field in the world, and the five-borough tour is unforgettable.

## Fast courses for PR chasers

If your goal is a personal best, these courses are known for being fast and flat:

- **Berlin Marathon** — The world's fastest marathon course. Flat, wide roads, perfect fall weather.
- **Chicago Marathon** — Another flat, fast course with massive crowd support.
- **Houston Marathon** — January 18, 2026. A fast winter race and a great early-season target.

## Destination races worth traveling for

Some races are as much about the experience as the finish time:

- **Comrades Marathon** (Durban, South Africa) — June 14, 2026. The world's oldest and largest ultramarathon. Approximately 87 km of rolling hills between Durban and Pietermaritzburg.
- **Marathon des Sables** (Sahara Desert, Morocco) — April 2026. Six days, 250 km across the Sahara. The toughest foot race on Earth.
- **Midnight Sun Marathon** (Tromsø, Norway) — June 2026. Run under the midnight sun above the Arctic Circle.

## NYRR and NYC races

New York Road Runners puts on dozens of races throughout the year. Key 2026 dates:

- **NYC Half Marathon** — March 15, 2026
- **Brooklyn Half Marathon** — May 16, 2026
- **NYC Marathon** — November 1, 2026

## Half marathons for every level

Not ready for a full marathon? These halfs are world-class:

- **Brooklyn Half** — One of the largest half marathons in the US. Finishes on the Coney Island boardwalk.
- **NYC Half** — A tour through Manhattan from Central Park to the Financial District.
- **Big Sur Half Marathon** — Stunning Pacific Coast Highway views.

## How to plan your year

The best approach is to pick one goal race and build your calendar around it. Use shorter races as tune-ups, and give yourself at least 16 weeks of dedicated training before your target marathon.

Explore the full 2026 race calendar on Runrora — filter by distance, location, and date to find your perfect race.
    `.trim(),
  },
  {
    slug: 'race-day-checklist-everything-you-need',
    title: 'Race Day Checklist: Everything You Need Before the Starting Line',
    author: 'Runrora Team',
    date: '2026-03-15',
    readTime: '5 min read',
    category: 'Race Prep',
    excerpt: 'Don\'t leave anything to chance on race morning. This checklist covers gear, nutrition, logistics, and the mental prep that separates good races from great ones.',
    heroImage: null,
    content: `
You've trained for months. The hay is in the barn. Now don't blow it by forgetting your watch charger or wearing new shoes on race day. Here's everything you need to think about before you toe the line.

## The week before

**Logistics to lock down:**
- Confirm your race bib pickup time and location (many races require day-before pickup)
- Plan your route to the start — parking, public transit, or shuttle options
- Check the weather forecast and plan your outfit accordingly
- If traveling, arrive at least one day early to adjust

**Gear to prepare:**
- Lay out everything you'll wear and carry on race day
- Charge your GPS watch fully
- Test your race outfit on a short run (never wear anything new on race day)
- Prepare your nutrition plan — gels, chews, salt tabs, whatever you've trained with

## The night before

- Set two alarms (phone + backup)
- Pin your bib to your shirt or singlet
- Attach timing chip to your shoe if it's a separate chip
- Pack a bag with: extra layers for before/after, phone, ID, cash, post-race snack
- Eat a familiar dinner — nothing adventurous
- Hydrate normally (don't over-drink)
- Get to bed early, but don't stress if you can't sleep — pre-race nerves are normal and one night of poor sleep won't affect performance

## Race morning

**2-3 hours before start:**
- Eat your pre-race meal (whatever you've practiced in training — toast, banana, oatmeal, bagel)
- Coffee if that's part of your routine
- Use the bathroom at home before heading out

**At the race venue:**
- Arrive 60-90 minutes before your wave
- Use the portable toilets early — lines get long
- Do a 10-15 minute warmup jog if it's a shorter race (half marathon or under)
- For a marathon, walking to the start is enough warmup
- Apply body glide or anti-chafe products to problem areas

## What to bring to the start line

**Essentials:**
- GPS watch (charged and satellite-locked)
- Race bib (visible on your front)
- Timing chip (on your shoe)
- Nutrition for the course (gels/chews in a belt or pockets)

**Nice to have:**
- Throwaway warm layer for the corral (old sweatshirt you don't mind donating)
- Sunglasses and sunscreen
- Hat or visor
- Headphones (check if the race allows them — many trail races don't)

**Leave behind:**
- Your phone (unless you want finish line photos — most race photographers will capture you)
- Anything you haven't tested in training

## Mental checklist

- Review your pacing plan one more time
- Remind yourself: the first 5K should feel easy. If it doesn't, you're going too fast.
- Have a mantra ready for when it gets hard (miles 18-22 in a marathon, the last 10 miles of an ultra)
- Focus on effort, not pace — conditions on race day may differ from training

## After the finish

- Keep walking for at least 10 minutes
- Hydrate and eat within 30 minutes
- Stretch gently
- Celebrate — you earned it

Track your upcoming races and build your race-day prep on Runrora.
    `.trim(),
  },
  {
    slug: 'why-running-communities-make-you-faster',
    title: 'Why Running Communities Make You Faster',
    author: 'Runrora Team',
    date: '2026-03-12',
    readTime: '5 min read',
    category: 'Community',
    excerpt: 'Solo running builds discipline. Community running builds speed, consistency, and the kind of accountability that gets you to the start line ready.',
    heroImage: null,
    content: `
There's a reason the best runners in the world train in groups. From the Bowerman Track Club to the NN Running Team to your local Tuesday track workout — community makes you better. Here's why.

## Accountability is the most underrated training tool

The hardest part of any training plan isn't the long run or the speed work — it's showing up consistently, week after week. When someone is waiting for you at the trailhead at 6am, you show up. When it's just you and your alarm clock, the snooze button wins more often than you'd like to admit.

Research backs this up: runners who train with a group consistently report higher adherence to their training plans. Having a set time and place to meet removes the daily decision of whether to run — you just show up.

## You'll run paces you wouldn't choose alone

Left to our own devices, most of us run the same comfortable pace every day. Group runs naturally introduce variety — someone pushes the pace on a hill, the group surges in the last mile, or a faster friend pulls you along on an interval workout.

This kind of incidental speedwork is how many runners break through plateaus. You don't need a structured plan if you're regularly running with people who push you just outside your comfort zone.

## Race intel you can't Google

Which porta-potty line is shortest at mile 3 of the NYC Marathon? Where does the wind hit hardest on the Chicago lakefront course? What's the real story on Heartbreak Hill — is it as bad as they say?

Experienced runners in your community have this knowledge, and they'll share it freely. Race reports, course tips, gear recommendations, and nutrition strategies — the collective wisdom of a running community is worth more than any training app.

## The social long run changes everything

The long run is the cornerstone of marathon and ultra training, but it's also the hardest session to execute alone. Three hours of solo running is mentally grueling. Three hours with friends? That's just a good Saturday morning.

Communities turn the hardest part of training into the most enjoyable part. And when you enjoy the process, you train more consistently, which makes you faster.

## Finding your people

Running communities come in many forms:

- **Local run clubs** — Most cities have free weekly group runs. Check your local running store.
- **Race communities** — Connect with other runners signed up for the same race. Share training updates, ask questions, and meet up on race day.
- **Online communities** — Follow runners training for similar goals. Share your progress, cheer others on, and stay motivated between group runs.

The best running community is the one that matches your pace, your goals, and your vibe. Some groups are competitive and fast. Others are social and supportive. Many are both.

## Start here

On Runrora, every race has its own community. When you register for a race, you're automatically connected with other runners doing the same event. Share training updates, ask questions, and find people to run with — both in person and online.

The miles you run alone build your body. The miles you run together build everything else.
    `.trim(),
  },
];

export default blogPosts;
